import { useState } from 'react';
import { useKaydedilmemisIcerik } from '../shell/KaydedilmemisIcerik';
import { Group, Select, Stack, Table, Text, TextInput } from '@mantine/core';
import type {
  KisiUygunlukPaneli,
  UygunlukDurumu,
  UygunlukKararSinifi,
  UygunlukSatiri,
} from '@isg/validation';
import {
  BELIRLENME_YOLLARI,
  GOREVLENDIRME_TEMSILCI_REF,
  UYGUNLUK_DURUM_ETIKETI,
  readGorevlendirmeKurali,
} from '@isg/validation';
import type { BelirlenmeYolu, KurulUyeTipi } from '@isg/validation';
import {
  GOREVLENDIRME_KURUL_UYESI_REF,
  KURUL_UYE_TIPI_ETIKETI,
  KURUL_UYE_TIPLERI,
} from '@isg/validation';
import { BELIRLENME_YOLU_ETIKET } from '../i18n/tr';
import {
  useEmployments,
  useGorevlendirmeEkle,
  useGorevlendirmeKaldir,
  useGorevlendirmeler,
  useKisiUygunluk,
  useLookup,
  usePersonsSayfali,
  useWorkplacesTumu,
  useGorevlendirmeKapisi,
  useSahaGunu,
} from '../api/queries';
import { tarihGoster } from '../lib/format';
import { Bolum, KapiSebebi, Rozet, SatirEylemleri, SolukSatir } from '../sozlesme';
import { ANLAM } from '../theme';
import type { RozetAnlami } from '../theme';
import { BosListeNotu, QueryStatus } from './parts';

/**
 * GÖREVLENDİRME YÜZEYLERİ — kişi kartının iki bölümü (Tur 4½ / Aşama B;
 * Kapsam §25, kurallar `.claude/rules/gorevlendirme.md`).
 *
 * **DOSYANIN TEK CÜMLESİ: GÖREVLENDİRME BİR ŞAPKA DEĞİLDİR.** Bu dosya
 * `yetki-parts.tsx`ten **hiçbir şey** import etmez ve etmemelidir: şapka
 * *"sistemde ne yapabilirsin"*, görevlendirme *"sahada hangi görevi
 * taşıyorsun"* der. İkisi kişi kartında **yan yana** durur ve birbirini
 * hiçbir zaman beslemez (§25/1 — `gorevlendirme.ts`in `roles.ts`ten import
 * etmemesi kuralının ekran kolu).
 *
 * ✏️ **GEZİNTİ ARTÇISI 2 / B1 (11.09.2026): SOLUK SATIR TEK BİLEŞENDİR, İKİ
 * YÜZEY ÇAĞIRIR.** Görevlendirmeler sekmesinde ekleme satırı **hiç yoktu**
 * (holding yöneticisinde de uzmanda da — yetki kapısı değil ekran eksiği);
 * liste yalnız okunuyordu. Sekmeye inen satır kişi kartınınkiyle **AYNI
 * bileşendir** (`GorevlendirmeSolukSatiri`): aynı kanca (`useGorevlendirmeEkle`
 * → `POST persons/:id/designations`), aynı nesne ekseni kuralı, aynı temsilci
 * ve kurul alanları. Sekme yalnız önüne bir **kişi hücresi** ekler. Kurulun
 * üye satırı (G3.1) deseniyle aynı: **yüzey kısayolu, kayıt aynı** — ikinci
 * tablo, ikinci yüklem, ikinci yazma yolu YOK (kod denetimi
 * `apps/api/src/people/gorevlendirme-yazma-yolu-envanteri.spec.ts`).
 */

/** Görevlendirme YAZABİLEN şapkalar — TEK KAYNAK `@isg/validation` (uç ve
 *  kurul sayfasının üye satırı aynı listeyi okur — G3.1). */

/** Görünüm kısayolu — yazma kararı her istekte guard + bahçe kapısında. */
/**
 * ✏️ MADDE 226 · P13-b (12.09.2026): ~~`useGorevlendirmeYazabilir` (ekranda şapka
 * listesi) + `gorevlendirmeYazamamaMetni`~~ söküldü. Kişi kartı bölümü
 * `useGorevlendirmeKapisi(personId, employmentId)` — şapka → kişi bahçesi →
 * **arşiv** (seçili istihdamın işyeri); Görevlendirmeler sekmesi
 * `useKapilar().gorevlendirmeKapisi`. İkisi de sunucunun adım listesidir.
 */

/**
 * AKTİF istihdam görevlendirmenin eksenidir (§25/1): görevlendirme İSTİHDAMA
 * bağlanır, kişiye değil — işten çıkınca düşer. İki yüzey AYNI seçimi yapar
 * (bitişi boş ilk istihdam); ayrı yazılsaydı biri nakil sonrası farklı
 * istihdamı seçebilirdi.
 */
export function aktifIstihdamBul<T extends { bitis: string | null }>(
  employments: readonly T[] | undefined,
): T | null {
  return employments?.find((e) => e.bitis === null) ?? null;
}

/**
 * DÖRT DURUM → ANLAM (G6). Eşleme **tek yerde**; ekran renk yazmaz (P3-b).
 *
 * ⚠️ **`yaklasiyor` EKSİK DEĞİLDİR** ve rengi de öyle söyler: bugün geçerli
 * olan bir belge bir **uyarıdır**, bir eksiklik değil (§25/5).
 */
// ✏️ K18 (tur 5): ETİKET TEK SÖZLÜKTEN (`UYGUNLUK_DURUM_ETIKETI`) — günlük
// tarama ile panel aynı kelimeyi okur; burada yalnız ANLAM eşlenir (P3-b).
export const UYGUNLUK_ROZETI: Record<UygunlukDurumu, { anlam: RozetAnlami; etiket: string }> = {
  gecerli: { anlam: 'olumlu', etiket: UYGUNLUK_DURUM_ETIKETI.gecerli },
  yaklasiyor: { anlam: 'dikkat', etiket: UYGUNLUK_DURUM_ETIKETI.yaklasiyor },
  doldu: { anlam: 'gecikmis', etiket: UYGUNLUK_DURUM_ETIKETI.doldu },
  yok: { anlam: 'engel', etiket: UYGUNLUK_DURUM_ETIKETI.yok },
  // K9 — başlangıcı gelecekte: bugün taşınmaz, EKSİKTİR; `yaklasiyor` değil.
  henuz_degil: { anlam: 'gecikmis', etiket: UYGUNLUK_DURUM_ETIKETI.henuz_degil },
  // K8 — eski istihdamın temel İSG'si: yeni işe girişte yenilenmeli, EKSİKTİR.
  istihdam_oncesi: { anlam: 'gecikmis', etiket: UYGUNLUK_DURUM_ETIKETI.istihdam_oncesi },
};

/**
 * B2 — **KİŞİ UYGUNLUK PANELİ (G6): TAM LİSTE, eksikler değil.**
 *
 * *"Neyim eksik"* sorusu ancak *"neyim olmalı"* listesinin üstünde okunur —
 * yalnız eksikleri gösteren bir panel, hiç şartı olmayan kişiyle her şartı
 * tamam olan kişiyi **aynı boş ekranla** gösterirdi.
 *
 * ⚠️ **VERİ KAYNAĞI OLMAYAN BÖLÜM ÇİZİLMEZ.** KKD zimmeti bölümü bugün
 * YOKTUR ve boş bir başlıkla da uydurulmaz (§2.40 — veri kaynağı olmayan
 * kontrol yalan söyler). Yapı **bölümlüdür** ki KKD modülü gelince panel
 * yeniden yazılmasın (ERD §12/44).
 *
 * ⚠️ **SAĞLIK BÖLÜMÜ GEREKÇE TAŞIMAZ** (§2.3): yalnız VARLIK ve VADE görünür;
 * tanı · bulgu · tetkik buraya girmez ve girmemelidir.
 */
export function UygunlukPaneli({ personId }: { personId: string }): React.JSX.Element {
  const panel = useKisiUygunluk(personId);
  return (
    <Bolum baslik="Uygunluk" testId="kisi-uygunluk">
      <QueryStatus queries={[panel]} />
      {panel.data && (
        <Stack gap="md">
          <UygunlukBolumu
            baslik="Eğitimler"
            satirlar={panel.data.egitimler}
            testId="uygunluk-egitimler"
          />
          {/* S1 (tur 5): sağlık bölümü SAGLIK_OKUMA_SAPKALARI ile hizalı (YP-5:
              veya kişinin KENDİ kartı — karar sunucuda) — sunucu
              okuyamayan şapkaya `null` yollar ve bölüm ÇİZİLMEZ (veri kaynağı
              olmayan bölüm gösterilmez). S2: başlık ayrımı söyler — bu kutu
              uygunluk ŞARTIDIR, kişi kartındaki "Hekim kanaati ve kısıtlar"
              kutusu ise kanaatin kendisidir. */}
          {panel.data.saglik !== null && (
            <UygunlukBolumu
              baslik="Sağlık (uygunluk şartı)"
              satirlar={panel.data.saglik}
              testId="uygunluk-saglik"
            />
          )}
          {/* ✏️ MADDE 301 (01.10.2026): kişi KENDİ kartında kanaatin kararını ve
              kısıtlarını görür. Alan yalnız kimlik kolunda iner — başkasının
              kartında YOKTUR (karar sunucuda); ekran ikinci bir kapı yazmaz. */}
          {panel.data.kendiKanaat !== undefined && (
            <KendiKanaatBolumu kanaat={panel.data.kendiKanaat} />
          )}
          <UygunlukBolumu
            baslik="Görevlendirmeler"
            satirlar={panel.data.gorevlendirmeler}
            testId="uygunluk-gorevlendirmeler"
          />
        </Stack>
      )}
      <Text size="xs" c="dimmed" mt="xs">
        Bu liste kişinin taşıması gereken belgelerin tamamıdır; her satırın yanında neden istendiği
        yazar. Sağlık satırında yalnız geçerlilik görünür — muayene sonucu sistemde tutulmaz.
      </Text>
    </Bolum>
  );
}

const KARAR_ANLAMI: Record<UygunlukKararSinifi, RozetAnlami> = {
  uygun: 'olumlu',
  kisitli: 'dikkat',
  uygun_degil: 'engel',
};

/**
 * MADDE 301 — kendi kanaatin KARARI + KISIT cümleleri (kanaat belgesinin
 * cümlesi, `kisitIfadesi` — sunucuda kurulur). Tanı · bulgu · hekim notu YOK.
 */
function KendiKanaatBolumu({
  kanaat,
}: {
  kanaat: NonNullable<KisiUygunlukPaneli['kendiKanaat']> | null;
}): React.JSX.Element {
  return (
    <Stack gap={4} data-testid="uygunluk-kendi-kanaat">
      <Text fw={600} size="sm">
        Kanaat kararı
      </Text>
      {kanaat === null ? (
        <BosListeNotu mesaj="Kanaat kaydı yok." />
      ) : (
        <>
          <Group gap="xs">
            <Rozet
              anlam={kanaat.kararSinifi ? KARAR_ANLAMI[kanaat.kararSinifi] : 'bilgi'}
              testId="kendi-kanaat-karari"
            >
              {kanaat.kararAd}
            </Rozet>
          </Group>
          {kanaat.kisitlar.map((kisit) => (
            <Text key={kisit} size="sm" data-testid="kendi-kanaat-kisiti">
              {kisit}
            </Text>
          ))}
        </>
      )}
    </Stack>
  );
}

/**
 * ⚠️ **BÖLÜM "KAYNAK BOŞ MU" DİYE DEĞİL, VERİSİ VAR MI DİYE ÇİZİLİR.** Temel
 * İSG **herkese** sorulur; bir görevlendirme de onu isteyince satır bölüm
 * değiştirmez, yalnız gerekçe listesi zenginleşir (§25/6). Bölümleme
 * sunucudadır — ekran ikinci kez bölmez.
 */
function UygunlukBolumu({
  baslik,
  satirlar,
  testId,
}: {
  baslik: string;
  satirlar: readonly UygunlukSatiri[];
  testId: string;
}): React.JSX.Element | null {
  if (satirlar.length === 0) return null;
  return (
    <Stack gap={4} data-testid={testId}>
      <Text fw={600} size="sm">
        {baslik}
      </Text>
      <Table striped>
        <Table.Thead>
          <Table.Tr>
            <Table.Th>Belge</Table.Th>
            <Table.Th>Geçerlilik</Table.Th>
            <Table.Th>Durum</Table.Th>
            <Table.Th>Neden isteniyor</Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {satirlar.map((satir) => {
            const rozet = UYGUNLUK_ROZETI[satir.durum];
            return (
              <Table.Tr key={satir.anahtar} data-testid="uygunluk-satiri">
                <Table.Td>{satir.ad}</Table.Td>
                <Table.Td>
                  {satir.gecerlilikBitis === null ? '—' : tarihGoster(satir.gecerlilikBitis)}
                </Table.Td>
                <Table.Td>
                  <Rozet anlam={rozet.anlam} testId="uygunluk-rozeti">
                    {rozet.etiket}
                  </Rozet>
                </Table.Td>
                <Table.Td>
                  <Text size="xs" c="dimmed">
                    {satir.kaynakGorevlendirmeler.length === 0
                      ? 'Herkesten istenir'
                      : satir.kaynakGorevlendirmeler.join(' · ')}
                  </Text>
                </Table.Td>
              </Table.Tr>
            );
          })}
        </Table.Tbody>
      </Table>
    </Stack>
  );
}

/**
 * B1 — **KİŞİ KARTININ GÖREVLENDİRME BÖLÜMÜ.**
 *
 * Görevlendirme bir **ALT KALEMDİR** (P1'in yapısal ayrımı: *kendi sayfası
 * olabilir mi?* — olamaz, kişisiyle birlikte okunur) → **satır içi** girilir,
 * *"Ekle"* düğmesi yoktur (P1-a).
 *
 * ⚠️ **KALDIRMA SİLME DEĞİLDİR** (§25/1): kaldırılan satır **listede kalır**,
 * soluklaşır ve kaldırma tarihini gösterir. *"Olay tarihinde ekipte kim
 * vardı"* sorusu ancak böyle cevaplanır — satırı gizleyen ekran, veriyi
 * silmeden **görünmez** yapardı.
 *
 * ⚠️ **EKSİK DAMGASI SUNUCUDAN GELİR** ve ekranda **yeniden hesaplanmaz**:
 * damga türetilmiş bir değerdir (§25/5) ve iki yerde hesaplanan bir türetme,
 * birine eklenen bir şartın diğerine sessizce eklenmemesiyle ayrışır (§2.40).
 *
 * ✏️ **GEZİNTİ ARTÇISI 2 / B3 envanteri:** yazamayan şapkada bölüm yalnız
 * başlık satırlı boş bir tabloydu — *"kayıt yok"* bile demiyordu. Artık salt
 * okunur + sebep.
 */
export function GorevlendirmeBolumu({ personId }: { personId: string }): React.JSX.Element {
  const gorevlendirmeler = useGorevlendirmeler(personId);
  const employments = useEmployments(personId);
  const aktifIstihdam = aktifIstihdamBul(employments.data);
  // MADDE 226 — kapı SUNUCUDAN: şapka → kişi bahçesi → ARŞİV (aktif istihdamın işyeri).
  const gorevlendirmeKapisi = useGorevlendirmeKapisi(personId, aktifIstihdam?.id ?? null);
  const yazabilir = gorevlendirmeKapisi.data?.yazabilir ?? false;
  const kaldir = useGorevlendirmeKaldir(personId);
  // Madde 302: kaldırma günü SAHA GÜNÜ (sunucudan), tarayıcı günü değil.
  const sahaGunu = useSahaGunu();
  const [hata, setHata] = useState<string | null>(null);

  return (
    // ⚠️ AÇIKLAMA SÖZLEŞMENİN KENDİ SLOTUNDAN (`aciklama`) verilir, gövdeye
    // serbest bir <Text> olarak konmaz: gövdeye konan metin tablo ile başlık
    // arasında AKIŞ İÇİNDE durur ve kart yüksekliği değiştikçe altındaki
    // satırları oynatır — açılmış bir açılır listenin altından zemin çekilir.
    <Bolum
      baslik="Görevlendirmeler"
      aciklama="Görevlendirme bir yetki değil, sahadaki bir görevdir; sistemde hiçbir kapı açmaz. Kayıt istihdama bağlıdır — kişi işten ayrılınca düşer."
      testId="kisi-gorevlendirmeler"
    >
      <QueryStatus queries={[gorevlendirmeler, employments]} />
      {gorevlendirmeler.data && (gorevlendirmeler.data.length > 0 || yazabilir) && (
        <Table striped>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Görev</Table.Th>
              <Table.Th>Ekipman tipi</Table.Th>
              <Table.Th>İşyeri</Table.Th>
              <Table.Th>Başlangıç</Table.Th>
              <Table.Th>Durum</Table.Th>
              {yazabilir && <Table.Th w={44} aria-label="Satır eylemi" />}
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {gorevlendirmeler.data.map((g) => (
              <Table.Tr
                key={g.id}
                data-testid="gorevlendirme-satiri"
                // Kaldırılmış satır SİLİNMEZ, SOLUKLAŞIR — geçmiş görünür kalır.
                style={g.kaldirmaTarihi ? { opacity: 0.55 } : undefined}
              >
                <Table.Td>
                  <Group gap={6} wrap="nowrap">
                    <Text size="sm">{g.gorevlendirmeTuruAd}</Text>
                    {g.basTemsilci && (
                      <Rozet anlam="bilgi" testId="gorevlendirme-bas-temsilci">
                        Baş temsilci
                      </Rozet>
                    )}
                    {g.belirlenmeYolu && (
                      <Text size="xs" c="dimmed" data-testid="gorevlendirme-belirlenme-yolu">
                        {BELIRLENME_YOLU_ETIKET[g.belirlenmeYolu]}
                      </Text>
                    )}
                    {g.kurulUyeTipi && (
                      <Rozet anlam="bilgi" testId="gorevlendirme-kurul-uye-tipi">
                        {KURUL_UYE_TIPI_ETIKETI[g.kurulUyeTipi]}
                      </Rozet>
                    )}
                  </Group>
                </Table.Td>
                <Table.Td>{g.ekipmanTipiAd ?? '—'}</Table.Td>
                <Table.Td>{g.workplaceAd}</Table.Td>
                <Table.Td>{tarihGoster(g.baslangicTarihi)}</Table.Td>
                <Table.Td>
                  {g.kaldirmaTarihi ? (
                    <Rozet anlam="bilgi" testId="gorevlendirme-kaldirildi">
                      {tarihGoster(g.kaldirmaTarihi)} tarihinde kaldırıldı
                    </Rozet>
                  ) : g.eksik ? (
                    <Group gap={6} wrap="nowrap">
                      <Rozet anlam="gecikmis" testId="gorevlendirme-eksik">
                        Eksik
                      </Rozet>
                      {/* ⚠️ DAMGANIN GEREKÇESİ YANINDA (§2.40 sunum kolu):
                          "eksik" diyip neyin eksik olduğunu söylemeyen bir
                          rozet, kullanıcıyı panele göndermek zorunda bırakır. */}
                      <Text size="xs" c="dimmed" data-testid="gorevlendirme-eksik-sebep">
                        {g.eksikSartlar.map((s) => s.ad).join(' · ')}
                      </Text>
                    </Group>
                  ) : (
                    <Rozet anlam="olumlu" testId="gorevlendirme-tam">
                      Tam
                    </Rozet>
                  )}
                </Table.Td>
                {yazabilir && (
                  <Table.Td>
                    <SatirEylemleri
                      testId="gorevlendirme-eylemleri"
                      digerleri={[
                        {
                          etiket: 'Görevden kaldır',
                          tehlikeli: true,
                          gizli: g.kaldirmaTarihi !== null,
                          onClick: () => {
                            setHata(null);
                            if (!sahaGunu.data) {
                              setHata('Saha günü alınamadı — sayfayı yenileyip tekrar deneyin.');
                              return;
                            }
                            kaldir.mutate(
                              { id: g.id, kaldirmaTarihi: sahaGunu.data },
                              { onError: (e) => setHata(e.message) },
                            );
                          },
                          testId: 'gorevlendirme-kaldir',
                        },
                      ]}
                    />
                  </Table.Td>
                )}
              </Table.Tr>
            ))}
            {yazabilir && aktifIstihdam && (
              <GorevlendirmeSolukSatiri
                personId={personId}
                aktifIstihdam={aktifIstihdam}
                kolonSayisi={5}
                onHata={setHata}
                isyeriHucresi={
                  // İşyeri SORULMAZ: görevlendirme İSTİHDAMA bağlanır ve
                  // istihdam zaten tek bir işyerine bağlıdır (madde 80) —
                  // ikinci kez sormak çelişebilen bir kopya üretirdi.
                  <Text size="xs" c="dimmed">
                    Aktif istihdamdan
                  </Text>
                }
              />
            )}
          </Table.Tbody>
        </Table>
      )}
      {!yazabilir && gorevlendirmeler.data?.length === 0 && (
        <BosListeNotu mesaj="Kayıtlı görevlendirme yok." />
      )}
      {/* ⚠️ PASİF DÜĞMENİN SEBEBİ YANINDA (§2.40): istihdamı olmayan kişide
          soluk satır hiç çizilmez ve NEDEN çizilmediği SÖYLENİR. */}
      {yazabilir && employments.isSuccess && !aktifIstihdam && (
        <BosListeNotu mesaj="Görevlendirme aktif bir istihdama bağlanır; bu kişinin aktif istihdamı yok." />
      )}
      {gorevlendirmeler.data && gorevlendirmeKapisi.data && (
        <KapiSebebi
          durum={gorevlendirmeKapisi.data}
          nesne="Görevlendirmeyi"
          fiil="verir"
          testId="gorevlendirme-yazamama-sebebi"
        />
      )}
      {hata && (
        <Text size="xs" c={ANLAM.engel} role="alert" mt="xs" data-testid="gorevlendirme-hata">
          {hata}
        </Text>
      )}
    </Bolum>
  );
}

/**
 * ✏️ **GEZİNTİ ARTÇISI 2 / B1 — GÖREVLENDİRME SOLUK SATIRI (TEK BİLEŞEN).**
 *
 * Kişi kartı ve Görevlendirmeler sekmesi bunu çağırır; kişi hücresi yalnız
 * sekmede verilir. ⚠️ **İkinci bir ekleme satırı YAZILMAZ** — nesne ekseni
 * alanı, temsilci ve kurul alanları, istihdam ekseni kuralları burada TEK
 * yerde yaşar; kopya satır bir gün birine eklenen alanı diğerinde eksik
 * bırakırdı (§2.40'ın tek-yüklem kuralının ekran kolu).
 */
export function GorevlendirmeSolukSatiri({
  personId,
  aktifIstihdam,
  kisiHucresi,
  isyeriHucresi,
  kisiDokunuldu = false,
  kolonSayisi,
  onKaydedildi,
  onHata,
}: {
  /** `null` = kişi henüz seçilmedi (yalnız sekmede). */
  personId: string | null;
  aktifIstihdam: { id: string } | null;
  kisiHucresi?: React.ReactNode;
  isyeriHucresi: React.ReactNode;
  kisiDokunuldu?: boolean;
  kolonSayisi: number;
  onKaydedildi?: () => void;
  onHata: (mesaj: string | null) => void;
}): React.JSX.Element {
  const turler = useLookup('gorevlendirme_turu');
  const ekipmanTipleri = useLookup('equipment_tip');
  const ekle = useGorevlendirmeEkle(personId ?? '');

  const [turId, setTurId] = useState<string | null>(null);
  const [ekipmanTipiId, setEkipmanTipiId] = useState<string | null>(null);
  const [baslangic, setBaslangic] = useState('');
  // 5½ / B1 — yalnız çalışan temsilcisi türünde sorulur (§25/13); tür
  // REF'ten tanınır, adından değil.
  const [basTemsilci, setBasTemsilci] = useState(false);
  const [belirlenmeYolu, setBelirlenmeYolu] = useState<BelirlenmeYolu | null>(null);
  // İSG kurulu (§28/C2) — alt tip YALNIZ "Kurul üyesi" türünde sorulur ve ZORUNLU.
  const [kurulUyeTipi, setKurulUyeTipi] = useState<KurulUyeTipi | null>(null);

  // ⚠️ NESNE EKSENİ TÜRÜN KURALINDAN OKUNUR (G3) — ekran ikinci bir kural
  // yazmaz; sunucudaki kapı (`gorevlendirmeNesneIhlalleri`) ve buradaki alan
  // görünürlüğü AYNI yüklemden beslenir (§2.40'ın tek-yüklem kuralı).
  const secilenTur = (turler.data ?? []).find((t) => t.id === turId);
  const kural = readGorevlendirmeKurali(secilenTur?.kurallar ?? null);
  const tipGerekli = kural.nesneEkseni === 'ekipman_tipi';
  const temsilciTuru = secilenTur?.kutuphaneRef === GOREVLENDIRME_TEMSILCI_REF;
  const kurulTuru = secilenTur?.kutuphaneRef === GOREVLENDIRME_KURUL_UYESI_REF;

  const dokunuldu = kisiDokunuldu || turId !== null || baslangic !== '';
  // P12 (madde 224) — kaydedilmemiş görevlendirme satırı çıkışta uyarır (yönlendirici tek noktada).
  useKaydedilmemisIcerik(dokunuldu ? 1 : 0, 'görevlendirme satırı');
  const kaydedilebilir =
    personId !== null &&
    aktifIstihdam !== null &&
    turId !== null &&
    baslangic !== '' &&
    (!tipGerekli || ekipmanTipiId !== null) &&
    (!kurulTuru || kurulUyeTipi !== null);

  function sifirla(): void {
    setTurId(null);
    setEkipmanTipiId(null);
    setBaslangic('');
    setBasTemsilci(false);
    setBelirlenmeYolu(null);
    setKurulUyeTipi(null);
  }

  return (
    <SolukSatir
      testId="gorevlendirme-soluk-satir"
      dokunuldu={dokunuldu}
      kaydedilebilir={kaydedilebilir}
      kaydediliyor={ekle.isPending}
      kolonSayisi={kolonSayisi}
      // 5½ / B1 — temsilci alanları YALNIZ türün kütüphane temsilci
      // kalemi seçilince çizilir (§2.40: çalışamayacak kontrol görünmez;
      // başka türde sunucu 422 verir).
      ikinciKatman={
        temsilciTuru ? (
          <Group gap="md" wrap="wrap" data-testid="gorevlendirme-temsilci-alanlari">
            <Select
              size="xs"
              clearable
              comboboxProps={{ withinPortal: true }}
              placeholder="Belirlenme yolu"
              aria-label="Belirlenme yolu"
              data-testid="gorevlendirme-belirlenme-yolu-secim"
              value={belirlenmeYolu}
              onChange={(d) => setBelirlenmeYolu((d as BelirlenmeYolu | null) ?? null)}
              data={BELIRLENME_YOLLARI.map((y) => ({
                value: y,
                label: BELIRLENME_YOLU_ETIKET[y],
              }))}
            />
            <Select
              size="xs"
              allowDeselect={false}
              comboboxProps={{ withinPortal: true }}
              aria-label="Baş temsilci"
              data-testid="gorevlendirme-bas-temsilci-secim"
              value={basTemsilci ? 'evet' : 'hayir'}
              onChange={(d) => setBasTemsilci(d === 'evet')}
              data={[
                { value: 'hayir', label: 'Temsilci' },
                { value: 'evet', label: 'Baş temsilci' },
              ]}
            />
          </Group>
        ) : kurulTuru ? (
          <Group gap="md" wrap="wrap" data-testid="gorevlendirme-kurul-alanlari">
            <Select
              size="xs"
              comboboxProps={{ withinPortal: true }}
              placeholder="Kurul üye tipi (zorunlu)"
              aria-label="Kurul üye tipi"
              data-testid="gorevlendirme-kurul-uye-tipi-secim"
              value={kurulUyeTipi}
              onChange={(d) => setKurulUyeTipi((d as KurulUyeTipi | null) ?? null)}
              data={KURUL_UYE_TIPLERI.map((t) => ({
                value: t,
                label: KURUL_UYE_TIPI_ETIKETI[t],
              }))}
            />
          </Group>
        ) : undefined
      }
      onKaydet={() => {
        if (!kaydedilebilir || turId === null || aktifIstihdam === null) return;
        onHata(null);
        ekle.mutate(
          {
            employmentId: aktifIstihdam.id,
            gorevlendirmeTuruId: turId,
            ekipmanTipiId: tipGerekli ? ekipmanTipiId : null,
            baslangicTarihi: baslangic,
            ...(temsilciTuru ? { basTemsilci, belirlenmeYolu } : {}),
            ...(kurulTuru ? { kurulUyeTipi } : {}),
          },
          {
            onSuccess: () => {
              sifirla();
              onKaydedildi?.();
            },
            onError: (e) => onHata(e.message),
          },
        );
      }}
    >
      {kisiHucresi}
      <Table.Td>
        <Select
          size="xs"
          searchable
          // ⚠️ **AÇILIR LİSTE PORTALA ÇİZİLİR — SÜS DEĞİL, ZORUNLU.**
          // `Bolum` bir `Card`tır ve Mantine kartı içeriğini
          // KIRPAR; portalsız açılan liste kartın kenarında kesilir
          // ve yukarı açıldığında **tablo başlığının ARKASINA**
          // düşer — kullanıcı seçeneği görür ama TIKLAYAMAZ
          // (§2.40'ın en sinsi hâli: kontrol görünüyor, çalışmıyor).
          // E2E bunu ölçtü ve *"seçenek tıklanmıyor"* diye düştü.
          // Emsal: `sozlesme/SatirEylemleri`nin `Menu`sü de
          // `withinPortal` taşır — aynı sebeple.
          comboboxProps={{ withinPortal: true }}
          placeholder="Görev seç"
          aria-label="Görevlendirme türü"
          data-testid="gorevlendirme-soluk-tur"
          value={turId}
          onChange={(deger) => {
            setTurId(deger);
            setEkipmanTipiId(null);
            setBasTemsilci(false);
            setBelirlenmeYolu(null);
            setKurulUyeTipi(null);
          }}
          data={(turler.data ?? [])
            .filter((t) => !t.gizli)
            .map((t) => ({ value: t.id, label: t.ad }))}
        />
      </Table.Td>
      <Table.Td>
        {/* ⚠️ ALAN YALNIZ TÜRÜN KURALI İSTİYORSA ÇİZİLİR (§2.40:
            çalışamayacak kontrol görünmez olur) — işyeri düzeyi bir
            görevlendirmede seçilen tip sunucuda 422'ye çarpardı. */}
        {tipGerekli ? (
          <Select
            size="xs"
            searchable
            comboboxProps={{ withinPortal: true }}
            placeholder="Ekipman tipi"
            aria-label="Ekipman tipi"
            data-testid="gorevlendirme-soluk-tip"
            value={ekipmanTipiId}
            onChange={setEkipmanTipiId}
            data={(ekipmanTipleri.data ?? [])
              .filter((t) => !t.gizli)
              .map((t) => ({ value: t.id, label: t.ad }))}
          />
        ) : (
          <Text size="xs" c="dimmed">
            {turId === null ? '—' : 'İşyeri düzeyi'}
          </Text>
        )}
      </Table.Td>
      <Table.Td>{isyeriHucresi}</Table.Td>
      <Table.Td>
        <TextInput
          size="xs"
          type="date"
          aria-label="Başlangıç tarihi"
          data-testid="gorevlendirme-soluk-baslangic"
          value={baslangic}
          onChange={(olay) => setBaslangic(olay.currentTarget.value)}
        />
      </Table.Td>
      <Table.Td />
    </SolukSatir>
  );
}

/**
 * ✏️ **GEZİNTİ ARTÇISI 2 / B1 — SEKMENİN SOLUK SATIRI: kişi ara → görev seç →
 * (ekipman tipi) → başlangıç → ekle.**
 *
 * Kişi **sunucuda aranır** (`GET persons/sayfali?istihdam=aktif&q=`) — bahçe
 * ve aktiflik süzgeci uçtadır, ekran ikinci filtre yazmaz (§2.16). Seçilen
 * kişinin aktif istihdamı kişi kartıyla **aynı seçimle** bulunur
 * (`aktifIstihdamBul`); işyeri o istihdamdan okunur, sorulmaz (madde 80).
 * Kayıt sonrası kişi de boşalır: sekmede art arda giriş **farklı kişilere**
 * yapılır (kişi kartında kişi sabittir).
 */
export function GorevlendirmeListeSolukSatiri({
  onHata,
}: {
  onHata: (mesaj: string | null) => void;
}): React.JSX.Element {
  const [personId, setPersonId] = useState<string | null>(null);
  const [secilen, setSecilen] = useState<{ value: string; label: string } | null>(null);
  const [arama, setArama] = useState('');
  const kisiler = usePersonsSayfali({
    istihdam: 'aktif',
    q: arama.trim(),
    sayfa: 1,
    sayfaBoyutu: 20,
  });
  const employments = useEmployments(personId ?? '', { enabled: personId !== null });
  const isyerleri = useWorkplacesTumu();
  const aktifIstihdam = personId !== null ? aktifIstihdamBul(employments.data) : null;
  const isyeriAd = aktifIstihdam
    ? ((isyerleri.data ?? []).find((w) => w.id === aktifIstihdam.workplaceId)?.ad ?? '—')
    : null;

  const secenekler = (kisiler.data?.satirlar ?? []).map((p) => ({ value: p.id, label: p.adSoyad }));
  if (secilen && !secenekler.some((s) => s.value === secilen.value)) secenekler.unshift(secilen);

  return (
    <GorevlendirmeSolukSatiri
      personId={personId}
      aktifIstihdam={aktifIstihdam}
      kisiDokunuldu={personId !== null}
      kolonSayisi={6}
      onHata={onHata}
      onKaydedildi={() => {
        setPersonId(null);
        setSecilen(null);
        setArama('');
      }}
      kisiHucresi={
        <Table.Td>
          <Select
            size="xs"
            searchable
            clearable
            comboboxProps={{ withinPortal: true }}
            placeholder="Kişi ara"
            aria-label="Kişi"
            data-testid="gorevlendirme-soluk-kisi"
            value={personId}
            searchValue={arama}
            onSearchChange={setArama}
            // Süzgeç SUNUCUDA koşar; istemci ikinci kez süzmez.
            filter={({ options }) => options}
            nothingFoundMessage={kisiler.isFetching ? 'Aranıyor…' : 'Aktif istihdamlı kişi yok'}
            onChange={(deger, secenek) => {
              setPersonId(deger);
              setSecilen(deger ? { value: deger, label: secenek.label } : null);
            }}
            data={secenekler}
          />
        </Table.Td>
      }
      isyeriHucresi={
        <Text size="xs" c="dimmed" data-testid="gorevlendirme-soluk-isyeri">
          {personId === null
            ? '—'
            : aktifIstihdam
              ? isyeriAd
              : employments.isSuccess
                ? 'Aktif istihdam yok'
                : '…'}
        </Text>
      }
    />
  );
}
